import React, { useCallback, useEffect, useState } from 'react';
import { TextField, Button, Box, IconButton, MenuItem, Select, FormControl, InputLabel, Checkbox, ListItemText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { Dayjs } from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import { ListItem } from '@mui/material';
import { getMetadata, updateMetadata, uploadMetadata } from './DeliveryDateCalendar';
import { Loader } from '../../components/loader/Loader';
import { popupType, Region } from '../../types/types';
import { GenericPopUp } from '../../components/popup/genericPopUp';
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';

const DeliveryDateCalender = () => {
  const [loader, setLoader] = useState(false);
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [inputKey, setInputKey] = useState<string>('');
  const [regions, setRegions] = useState<Region[]>([{ code: '', holidays: [] }]);
  const [formattedRegions, setFormattedRegions] = useState<{ [key: string]: string[] }[]>([]);
  const [weekendDays, setWeekendDays] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const handleAddRegion = () => {
    setRegions([...regions, { code: '', holidays: [] }]);
  };

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const handleRegionChange = (index: number, value: string) => {
    const newRegions = [...regions];
    newRegions[index].code = value;
    setRegions(newRegions);
  };

  const handleHolidayChange = (index: number, date: Dayjs | null) => {
    if (date) {
      const formattedDate = date.format('YYYY-MM-DD');
      const newRegions = [...regions];
      if (!newRegions[index].holidays.includes(formattedDate)) {
        newRegions[index].holidays = [...newRegions[index].holidays, formattedDate];
        setRegions(newRegions);
      }
    }
  };

  const handleHolidayDelete = (regionIndex: number, holidayIndex: number) => {
    const newRegions = [...regions];
    newRegions[regionIndex].holidays.splice(holidayIndex, 1);
    setRegions(newRegions);
  };

  const getFormattedRegions = () => {
    const formatted: { [key: string]: string[] } = {};
    regions.forEach(region => {
      if (region.code) {
        formatted[region.code] = region.holidays;
      }
    });
    return [formatted];
  };

  const updateRegionState = () => {
    const formatted = getFormattedRegions();
    setFormattedRegions(formatted);
  };

  const handleSubmit = async () => {
    const requestBody = [
      {
        extendedAttributes: {
          field: inputKey
        },
        domainName: 'BusinessCalendar',
        domainType: 'fieldWiseHolidayCalender',
        domainValues: formattedRegions
      }
    ];
    let submitDetails;
    try {
      let metadata = await getMetadata();
      // if (metadata.features.length > 0) {
      //   submitDetails = await updateMetadata(requestBody[0]);
      // } else {
      //   submitDetails = await uploadMetadata(requestBody);
      // }

      // if (submitDetails.status === "Success") {
        // setLoader(true);
        // openPopup("Your information has been updated", "Success");
        // setLoader(false);
      // }
      let finalPayload = metadata.features.length > 0 ? requestBody[0] : requestBody
      validateWithOtp((verifiedOTPUser?: any,verifyResponse?: any)=>{
        if (verifyResponse.data.status === "Success" ){
          setLoader(true);
          openPopup("Your information has been updated", "Success");
          setLoader(false);
        }
      },undefined,"all","/metadata/BusinessCalendar?name=fieldWiseHolidayCalender",finalPayload,"PUT")
    } catch (e: any) {
      setLoader(true);
      openPopup(e?.data?.message, "Error");
      setLoader(false);
    }
  };

  useEffect(() => {
    const method = async () => {
      try {
        const response = await getMetadata();
        const parsedRegions = Object.keys(response.features[0].domainValues[0]).map(key => ({
          code: key,
          holidays: response.features[0].domainValues[0][key],
        }));
        setRegions(parsedRegions);
        setInputKey(response.features[0].extendedAttributes.field);
      } catch (error) {
        console.error('Error fetching existing data:', error);
      }
    };

    method();
  }, []);

  const getAllWeekends = (year:any, selectedDays:any) => {
    const weekends = [];
    const date = new Date(year, 0, 1); 
    while (date.getFullYear() === year) {
      if (selectedDays.includes(date.getDay())) { 
        weekends.push(date.toLocaleDateString('en-CA'));
      }
      date.setDate(date.getDate() + 1);
    }
    return weekends;
  };

  const handleSelectWeekends = (regionIndex:any) => {
    const currentYear = selectedYear;
    const weekends = getAllWeekends(currentYear, weekendDays);
    const newRegions = [...regions];
    newRegions[regionIndex].holidays = weekends;
    setRegions(newRegions);
  };

  const handleYearChange = (event:any) => {
    setSelectedYear(event.target.value);
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from(new Array(11), (val, index) => currentYear - 5 + index);


  const handleWeekendDaysChange = (event:any) => {
    const { value } = event.target;
    setWeekendDays(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {loader ? (
        <Loader />
      ) : (
        <Box className="App" sx={{ p: 4, overflowX: 'hidden' }}>
          <GenericPopUp
            message={genericMessage}
            type={popupType}
            openGenericModal={openGenericModal}
            setOpenGenericModal={setOpenGenericModal}
          />
          <h1>Holiday Planner</h1>
          <Box mb={2}>
            <TextField
              label="Field"
              value={inputKey}
              onChange={(e) => { setInputKey(e.target.value); updateRegionState() }}
              placeholder="Enter key (e.g., region, supplier)"
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <FormControl fullWidth>
              <InputLabel>Day off</InputLabel>
              <Select
                multiple
                value={weekendDays}
                onChange={handleWeekendDaysChange}
                renderValue={(selected) => selected.map(day => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day]).join(', ')}
                label="Day off"
              >
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, index) => (
                  <MenuItem key={index} value={index}>
                    <Checkbox checked={weekendDays.includes(index)} />
                    <ListItemText primary={day} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box mb={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Year"
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {regions.map((region, index) => (
            <Box key={index} className="region-input" mb={2}>
              <TextField
                label="Field Code"
                value={region.code}
                onChange={(e) => { handleRegionChange(index, e.target.value); updateRegionState() }}
                placeholder="Enter region code"
                fullWidth
                margin="normal"
              />
              <Box display='flex' flexDirection='row' sx={{ minWidth: '100vw' }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={null}
                  onChange={(date) => { handleHolidayChange(index, date); updateRegionState() }}
                  renderInput={(params) => <TextField {...params} />}
                  dayOfWeekFormatter={(day) => `${day}.`}
                  toolbarFormat="ddd DD MMMM"
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gridAutoRows: 'min-content',
                    gap: 2,
                    height: 400,
                    width: 1000,
                    overflowY: 'auto',
                    padding: 2,
                  }}
                >
                  {region.holidays.map((holiday, i) => (
                    <ListItem key={i} sx={{
                      border: '1px solid #ddd',
                      padding: '10px',
                      backgroundColor: '#f0f0f0',
                      marginBottom: '5px',
                    }}>
                      {holiday}
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => { handleHolidayDelete(index, i); updateRegionState() }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </Box>
              </Box>
              <Button variant="outlined" onClick={() => handleSelectWeekends(index)} sx={{ mt: 1 }}>
                Select All Day offs
              </Button>
            </Box>
          ))}
          <Button variant="contained" onClick={handleAddRegion} sx={{ mt: 2 }}>
            + Add Region
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ mt: 2 }}>
            Submit
          </Button>
        </Box>
      )}
    </LocalizationProvider>
  );
};

export default DeliveryDateCalender;
